import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { orderService } from '../services';

const OrderDetail = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadOrderDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    const loadOrderDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await orderService.getOrderById(orderId);
            if (response.success) {
                setOrder(response.order);
            } else {
                setError('Order not found');
            }
        } catch (err) {
            console.error('Error loading order:', err);
            setError('Failed to load order details');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                Loading order details...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '20px' }}>
                <div style={{ color: '#f44336', marginBottom: '20px' }}>{error}</div>
                <button
                    onClick={() => navigate(-1)}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Go Back
                </button>
            </div>
        );
    }

    if (!order) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                Order not found
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ marginBottom: '30px' }}>
                <button
                    onClick={() => navigate(-1)}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginBottom: '15px'
                    }}
                >
                    ← Back to Dashboard
                </button>
                <h1 style={{ margin: '10px 0' }}>Order Details</h1>
                <p style={{ color: '#666', fontSize: '14px' }}>
                    Order ID: {order.orderId}
                </p>
            </div>

            {/* Order Summary Card */}
            <div style={{
                backgroundColor: '#f5f5f5',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '30px'
            }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                    <div>
                        <h3 style={{ marginTop: 0, color: '#2196F3' }}>Customer Information</h3>
                        <p><strong>Name:</strong> {order.customerName || 'N/A'}</p>
                        <p><strong>Mobile:</strong> {order.customerMobile || 'N/A'}</p>
                    </div>
                    <div>
                        <h3 style={{ marginTop: 0, color: '#4CAF50' }}>Order Information</h3>
                        <p><strong>Date & Time:</strong> {new Date(order.orderDate).toLocaleString('en-IN', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                        })}</p>
                        <p><strong>Total Amount:</strong> <span style={{ fontSize: '20px', color: '#4CAF50', fontWeight: 'bold' }}>₹{order.totalAmount?.toFixed(2) || '0.00'}</span></p>
                    </div>
                </div>
            </div>

            {/* Order Items */}
            <div>
                <h2 style={{ marginBottom: '15px' }}>Order Items</h2>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#2196F3', color: 'white' }}>
                            <th style={{ padding: '12px', textAlign: 'left' }}>Product Name</th>
                            <th style={{ padding: '12px', textAlign: 'right' }}>Quantity</th>
                            <th style={{ padding: '12px', textAlign: 'right' }}>Unit</th>
                            <th style={{ padding: '12px', textAlign: 'right' }}>Price/Unit</th>
                            <th style={{ padding: '12px', textAlign: 'right' }}>Subtotal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {order.orderItems && order.orderItems.length > 0 ? (
                            order.orderItems.map((item, index) => (
                                <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
                                    <td style={{ padding: '12px' }}>{item.productName}</td>
                                    <td style={{ padding: '12px', textAlign: 'right' }}>{item.quantity}</td>
                                    <td style={{ padding: '12px', textAlign: 'right' }}>{item.unit}</td>
                                    <td style={{ padding: '12px', textAlign: 'right' }}>₹{item.pricePerUnit?.toFixed(2) || '0.00'}</td>
                                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>
                                        ₹{((item.quantity || 0) * (item.pricePerUnit || 0)).toFixed(2)}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                    No items in this order
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                            <td colSpan="4" style={{ padding: '15px', textAlign: 'right', fontSize: '16px' }}>
                                Total Amount:
                            </td>
                            <td style={{ padding: '15px', textAlign: 'right', fontSize: '20px', color: '#4CAF50' }}>
                                ₹{order.totalAmount?.toFixed(2) || '0.00'}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* Original Order Text (if available) */}
            {order.originalOrderText && (
                <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '8px' }}>
                    <h3 style={{ marginTop: 0 }}>Original Order (Voice/Text)</h3>
                    <p style={{ fontStyle: 'italic', margin: 0 }}>{order.originalOrderText}</p>
                </div>
            )}
        </div>
    );
};

export default OrderDetail;
